<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { differenceInDays, format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BillingAPI from 'dashboard/api/billing';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PlanResourcesCard from './components/PlanResourcesCard.vue';
import ConsumptionStatement from './components/ConsumptionStatement.vue';
import RechargeButton from './components/RechargeButton.vue';
import creditsAPI from 'dashboard/api/credits';

const { t } = useI18n();
const router = useRouter();

// State
const subscription = ref(null);
const currentPlan = ref(null);
const invoice = ref(null);
const isLoading = ref(true);
const isGeneratingInvoice = ref(false);
const isGeneratingPix = ref(false);
const isGeneratingBoleto = ref(false);
const isOpeningPortal = ref(false);
const isCreatingCheckout = ref(false);
const showPaymentOptions = ref(false);
const showPixQrCode = ref(false);
const showBoletoDetails = ref(false);
const pixCopied = ref(false);
const boletoCopied = ref(false);
const boletoLine = ref(null);
const boletoUrl = ref(null);
const isRequestingNfse = ref(false);
const nfseRequested = ref(false);
const creditBalanceBrl = ref(0);

// ==========================================
// COMPUTED - Dados da Assinatura
// ==========================================
const planName = computed(
  () => currentPlan.value?.name || t('BILLING.HEADER.FREE_PLAN')
);
const isStripeLinked = computed(
  () => subscription.value?.stripe_linked || false
);
const isAsaasLinked = computed(() => subscription.value?.asaas_linked || false);
const hasAsaasCustomer = computed(
  () => subscription.value?.asaas_customer_id || false
);
const activeGateway = computed(() => {
  if (isStripeLinked.value) return 'Stripe';
  if (isAsaasLinked.value) return 'Asaas';
  return null;
});
const isFree = computed(() => (currentPlan.value?.monthly_price || 0) === 0);
// Cartão recorrente: oferecido a quem o backend marcou como elegível (conta
// internacional, sem PIX/boleto) e que ainda não tem assinatura no Stripe.
const canSubscribeCard = computed(
  () =>
    !!subscription.value?.card_subscription_available &&
    !isStripeLinked.value &&
    !isFree.value
);
const isMonthly = computed(
  () => subscription.value?.billing_cycle === 'monthly'
);

// Preço pelo MODELO NOVO (2026-07): Starter R$297 (1 caixa + 2 usuários
// inclusos) + R$197/caixa extra + R$75/usuário extra. Espelha as constantes
// de AccountSubscription::RESOURCE_PRICES/RESOURCE_INCLUDED (backend).
const billingStore = useStore();
const billingInboxes = useMapGetter('inboxes/getInboxes');
const billingAgents = useMapGetter('agents/getAgents');
const BASE_PRICE = 297;
const INBOX_EXTRA = 197;
const USER_EXTRA = 75;
const INCLUDED_INBOXES = 1;
const INCLUDED_USERS = 2;
const extraInboxesCount = computed(() =>
  Math.max(0, (billingInboxes.value || []).length - INCLUDED_INBOXES)
);
const extraUsersCount = computed(() =>
  Math.max(0, (billingAgents.value || []).length - INCLUDED_USERS)
);

const effectivePrice = computed(() => {
  // Preço NEGOCIADO (custom_plan_price, já com desconto no backend) tem
  // prioridade: é o valor mensal fixo tudo-incluído e DEVE bater com a fatura.
  if (subscription.value?.has_custom_price) {
    return Number(subscription.value.effective_price) || 0;
  }
  // Total REAL vindo do backend (plano + recursos + MÓDULOS — mesma régua da
  // fatura). O cálculo local antigo ignorava módulos e mostrava só o plano.
  const total = Number(subscription.value?.monthly_total) || 0;
  if (total > 0) return total;
  return (
    BASE_PRICE +
    extraInboxesCount.value * INBOX_EXTRA +
    extraUsersCount.value * USER_EXTRA
  );
});
const hasDiscount = computed(() => subscription.value?.has_discount || false);
const discountPercentage = computed(
  () => subscription.value?.discount_percentage || 0
);

// ==========================================
// COMPUTED - Status (Ativo / Trial / Vencido)
// ==========================================
// subscription.status vem de billing/current (só cobre active/trialing/etc.
// via account_subscription — ver AccountSubscription#status). Quando não há
// assinatura "ativa" nesse sentido restrito mas existe uma fatura vencida
// (billing/current_invoice), tratamos como "Vencido" — sinal real, não
// inventado.
const effectiveStatusKey = computed(() => {
  if (subscription.value?.status) return subscription.value.status;
  if (invoice.value?.status === 'overdue') return 'past_due';
  if (invoice.value?.status === 'pending') return 'pending';
  return null;
});

const STATUS_TONE = {
  active: 'bg-n-teal-3 text-n-teal-11',
  trialing: 'bg-n-blue-3 text-n-blue-11',
  past_due: 'bg-n-ruby-3 text-n-ruby-11',
  pending: 'bg-n-amber-3 text-n-amber-11',
  cancelled: 'bg-n-slate-3 text-n-slate-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  incomplete: 'bg-n-amber-3 text-n-amber-11',
};

const statusToneClass = computed(
  () => STATUS_TONE[effectiveStatusKey.value] || 'bg-n-slate-3 text-n-slate-11'
);

const statusLabel = computed(() => {
  const key = effectiveStatusKey.value;
  if (!key) return t('BILLING.HEADER.STATUS.FREE');
  return t(`BILLING.HEADER.STATUS.${key.toUpperCase()}`);
});

// Datas - usar apenas a parte da data para evitar problemas de timezone
const cycleEndDate = computed(() => {
  if (subscription.value?.current_period_end) {
    // Pegar apenas YYYY-MM-DD para evitar problemas de timezone
    const dateStr = subscription.value.current_period_end.split('T')[0];
    return parseISO(dateStr);
  }
  return null;
});

// Havendo fatura em aberto, ela manda na data: é o vencimento impresso no
// boleto/PIX que o cliente tem em mãos. O fim do ciclo só aparece quando não há
// nada a pagar — senão a tela anuncia uma data e a cobrança traz outra.
const nextChargeDate = computed(() => {
  const dueDate = invoice.value?.due_date;
  const isOpen = ['pending', 'overdue'].includes(invoice.value?.status);
  if (isOpen && dueDate) return parseISO(dueDate.split('T')[0]);
  return cycleEndDate.value;
});

const formattedNextChargeDate = computed(() => {
  if (!nextChargeDate.value) return '-';
  return format(nextChargeDate.value, 'dd/MM/yyyy', { locale: ptBR });
});

const daysUntilRenewal = computed(() => {
  if (!nextChargeDate.value) return 0;
  // Comparar apenas as datas, sem hora
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(nextChargeDate.value);
  endDate.setHours(0, 0, 0, 0);
  return Math.max(0, differenceInDays(endDate, today));
});

// ==========================================
// COMPUTED - Fatura Atual
// ==========================================
const hasInvoice = computed(() => invoice.value !== null);
const isPending = computed(() => invoice.value?.status === 'pending');
const isOverdue = computed(() => invoice.value?.status === 'overdue');
const isPaid = computed(() => invoice.value?.status === 'paid');
const hasPix = computed(() => !!invoice.value?.pix_qr_code);
const needsPayment = computed(() => {
  return hasInvoice.value && (isPending.value || isOverdue.value);
});

const invoiceTotal = computed(
  () => invoice.value?.total_amount || effectivePrice.value
);
const invoiceDueDate = computed(() => {
  if (!invoice.value?.due_date) return null;
  return format(parseISO(invoice.value.due_date), 'dd/MM/yyyy', {
    locale: ptBR,
  });
});
const invoicePeriodRef = computed(() => invoice.value?.period_reference || '-');
const canGenerateInvoice = computed(() => {
  return !isFree.value && (!hasInvoice.value || isPaid.value);
});

// ==========================================
// COMPUTED - Próximo Pagamento
// ==========================================
const nextPaymentDate = computed(() => {
  // Pré-pago: a próxima cobrança é no FIM do ciclo atual (mesma data mostrada no
  // topo em "Próxima cobrança"). Não somar mês, senão fica 1 mês adiantado.
  return nextChargeDate.value;
});

const formattedNextPaymentDate = computed(() => {
  if (!nextPaymentDate.value) return '-';
  return format(nextPaymentDate.value, 'MMMM/yyyy', { locale: ptBR });
});

// ==========================================
// FORMATTERS
// ==========================================
const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const formatCreditBalance = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value || 0);

// ==========================================
// METHODS
// ==========================================
const fetchData = async () => {
  isLoading.value = true;
  try {
    const [subResponse, invoiceResponse, creditsResponse] = await Promise.all([
      BillingAPI.getCurrentSubscription(),
      BillingAPI.getCurrentInvoice(),
      creditsAPI.balance().catch(() => null),
    ]);
    subscription.value = subResponse.data.subscription;
    currentPlan.value = subResponse.data.plan;
    invoice.value = invoiceResponse.data.invoice;
    creditBalanceBrl.value = creditsResponse?.data?.balance_brl ?? 0;
    // Hydrate boleto data from persisted invoice
    if (invoice.value?.boleto_line) {
      boletoLine.value = invoice.value.boleto_line;
      boletoUrl.value = invoice.value.boleto_url;
      showBoletoDetails.value = true;
    }
    // Auto-show PIX if already generated
    if (invoice.value?.pix_qr_code) {
      showPixQrCode.value = true;
    }
  } catch {
    // Silent fail
  } finally {
    isLoading.value = false;
  }
};

const generateInvoice = async () => {
  isGeneratingInvoice.value = true;
  try {
    const response = await BillingAPI.generateInvoice();
    invoice.value = response.data.invoice;
    useAlert(t('BILLING.SUCCESS.INVOICE_GENERATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('BILLING.ERRORS.ACTION_FAILED'));
  } finally {
    isGeneratingInvoice.value = false;
  }
};

const generatePix = async () => {
  if (!invoice.value?.id) return;
  isGeneratingPix.value = true;
  try {
    const response = await BillingAPI.generatePixForInvoice(invoice.value.id);
    invoice.value = response.data.invoice;
    showPixQrCode.value = true;
    showPaymentOptions.value = false;
    useAlert(t('BILLING.PAYMENT.PIX_GENERATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('BILLING.PAYMENT.PIX_ERROR'));
  } finally {
    isGeneratingPix.value = false;
  }
};

const generateBoleto = async () => {
  if (!invoice.value?.id) return;
  isGeneratingBoleto.value = true;
  try {
    const response = await BillingAPI.generateBoletoForInvoice(
      invoice.value.id
    );
    invoice.value = response.data.invoice;
    // Read boleto data from invoice (now persisted in payment_details)
    boletoLine.value = invoice.value.boleto_line || null;
    boletoUrl.value = invoice.value.boleto_url || null;
    showBoletoDetails.value = true;
    showPaymentOptions.value = false;
    useAlert(t('BILLING.PAYMENT.BOLETO_GENERATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('BILLING.PAYMENT.BOLETO_ERROR'));
  } finally {
    isGeneratingBoleto.value = false;
  }
};

const payWithStripe = async () => {
  if (!currentPlan.value?.id) return;
  isCreatingCheckout.value = true;
  try {
    const response = await BillingAPI.createCheckout(
      currentPlan.value.id,
      subscription.value?.billing_cycle || 'monthly'
    );
    if (response.data.checkout_url) {
      window.location.href = response.data.checkout_url;
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING.PAYMENT.STRIPE_OPEN_ERROR')
    );
  } finally {
    isCreatingCheckout.value = false;
  }
};

// Assinatura recorrente no cartão: o cliente internacional não tem PIX/boleto,
// então esse é o caminho dele para sair da cobrança combinada por fora.
const subscribeWithCard = async () => {
  isCreatingCheckout.value = true;
  try {
    const response = await BillingAPI.subscribeCard();
    if (response.data.checkout_url) {
      window.location.href = response.data.checkout_url;
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING.PAYMENT.STRIPE_OPEN_ERROR')
    );
  } finally {
    isCreatingCheckout.value = false;
  }
};

const copyPixPayload = async () => {
  if (invoice.value?.pix_payload) {
    try {
      await navigator.clipboard.writeText(invoice.value.pix_payload);
      pixCopied.value = true;
      setTimeout(() => {
        pixCopied.value = false;
      }, 2000);
    } catch {
      // Fallback
    }
  }
};

const copyBoletoLine = async () => {
  if (boletoLine.value) {
    try {
      await navigator.clipboard.writeText(boletoLine.value);
      boletoCopied.value = true;
      setTimeout(() => {
        boletoCopied.value = false;
      }, 2000);
    } catch {
      // Fallback
    }
  }
};

const requestNfse = async () => {
  if (!invoice.value?.id) return;
  isRequestingNfse.value = true;
  try {
    await BillingAPI.requestNfse(invoice.value.id);
    nfseRequested.value = true;
    useAlert(t('BILLING.PAYMENT.NFSE_REQUESTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('BILLING.PAYMENT.NFSE_ERROR'));
  } finally {
    isRequestingNfse.value = false;
  }
};

const openStripePortal = async () => {
  isOpeningPortal.value = true;
  try {
    const response = await BillingAPI.createPortal();
    if (response.data.portal_url) {
      window.location.href = response.data.portal_url;
    }
  } catch {
    useAlert(t('BILLING.GATEWAY.PORTAL_ERROR'));
  } finally {
    isOpeningPortal.value = false;
  }
};

const goToHistory = () => {
  router.push({ name: 'billing_invoice_history' });
};

// Handle Stripe redirect results
const handleStripeRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const stripeResult = urlParams.get('stripe');
  if (stripeResult === 'success') {
    useAlert(t('BILLING.GATEWAY.STRIPE_SUCCESS'));
    window.history.replaceState({}, '', window.location.pathname);
  }
};

onMounted(() => {
  handleStripeRedirect();
  fetchData();
  billingStore.dispatch('inboxes/get');
  billingStore.dispatch('agents/get');
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING.SIMPLE.TITLE')"
        :description="$t('BILLING.SIMPLE.DESCRIPTION')"
        feature-name="billing"
      />
    </template>

    <template #body>
      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-16">
        <Spinner :size="32" />
      </div>

      <!-- Content -->
      <div v-else class="space-y-6">
        <!-- ==========================================
             1. CABEÇALHO: plano + status + próxima cobrança
        ========================================== -->
        <div
          class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden"
        >
          <div class="p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6">
            <div>
              <div class="flex items-center gap-2 flex-wrap mb-2">
                <span
                  v-if="activeGateway"
                  class="px-2.5 py-1 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-11"
                >
                  {{ activeGateway }}
                </span>
                <span
                  class="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5"
                  :class="statusToneClass"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-current" />
                  {{ statusLabel }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10 uppercase tracking-wide mb-1">
                {{ t('BILLING.HEADER.PLAN_LABEL') }}
              </p>
              <p class="text-2xl font-bold text-n-slate-12">
                {{ planName }}
              </p>
            </div>

            <div v-if="!isFree" class="text-left md:text-right">
              <p class="text-xs text-n-slate-10 uppercase tracking-wide mb-1">
                {{ t('BILLING.HEADER.MONTHLY_TOTAL') }}
              </p>
              <p class="text-3xl font-bold text-n-slate-12 tabular-nums">
                {{ formatCurrency(effectivePrice) }}
                <span class="text-sm font-normal text-n-slate-11">
                  {{ isMonthly ? t('BILLING.CYCLE.PER_MONTH') : '' }}
                </span>
              </p>
              <p v-if="hasDiscount" class="text-xs text-n-teal-11 mt-1">
                {{
                  t('BILLING.HEADER.DISCOUNT_APPLIED', {
                    percent: discountPercentage,
                  })
                }}
              </p>
              <p class="text-sm text-n-slate-11 mt-1">
                {{ t('BILLING.HEADER.NEXT_BILLING') }}:
                <span class="font-medium text-n-slate-12 tabular-nums">{{
                  formattedNextChargeDate
                }}</span>
                <span v-if="daysUntilRenewal" class="tabular-nums">
                  ({{
                    t('BILLING.STATUS.RENEWS_IN', { days: daysUntilRenewal })
                  }})
                </span>
              </p>
            </div>

            <div v-else class="text-left md:text-right">
              <p class="text-sm text-n-slate-11">
                {{ t('BILLING.HEADER.FREE_PLAN_HINT') }}
              </p>
            </div>
          </div>

          <!-- Stripe Portal Button -->
          <div v-if="isStripeLinked && !isFree" class="px-6 pb-6">
            <ButtonV4
              faded
              slate
              class="w-full"
              :is-loading="isOpeningPortal"
              @click="openStripePortal"
            >
              <span class="i-lucide-credit-card w-5 h-5 mr-2" />
              {{ t('BILLING.GATEWAY.MANAGE_STRIPE') }}
            </ButtonV4>
          </div>
        </div>

        <!-- ==========================================
             PAGAMENTO (fatura pendente / em dia) — só planos pagos
        ========================================== -->
        <div
          v-if="!isFree || needsPayment"
          class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden"
        >
          <div class="px-6 py-4 border-b border-n-weak bg-n-alpha-1">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('BILLING.PAYMENT.TITLE') }}
            </h2>
          </div>

          <div class="p-6">
            <!-- Fatura Pendente -->
            <div v-if="needsPayment">
              <div class="flex items-start justify-between mb-4">
                <div>
                  <p
                    class="text-xs text-n-slate-10 uppercase tracking-wide mb-1"
                  >
                    {{ t('BILLING.PAYMENT.CURRENT_INVOICE') }}
                  </p>
                  <p class="text-lg font-semibold text-n-slate-12">
                    {{ invoicePeriodRef }}
                  </p>
                </div>
                <span
                  :class="
                    isOverdue
                      ? 'bg-n-ruby-3 text-n-ruby-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                  class="px-3 py-1 rounded-full text-xs font-medium"
                >
                  {{
                    isOverdue
                      ? t('BILLING.INVOICE.STATUS.OVERDUE')
                      : t('BILLING.INVOICE.STATUS.PENDING')
                  }}
                </span>
              </div>

              <div class="p-4 rounded-xl bg-n-alpha-2 mb-4">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-n-slate-11">{{
                    t('BILLING.INVOICE.TOTAL')
                  }}</span>
                  <span class="text-xl font-bold text-n-slate-12 tabular-nums">
                    {{ formatCurrency(invoiceTotal) }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-n-slate-11">{{
                    t('BILLING.INVOICE.DUE_DATE')
                  }}</span>
                  <span
                    class="text-sm font-medium text-n-slate-12 tabular-nums"
                  >
                    {{ invoiceDueDate }}
                  </span>
                </div>
              </div>

              <!-- Botão principal PAGAR (quando não tem nada aberto ainda) -->
              <!-- Cliente sem PIX/boleto (internacional): assinatura no cartão -->
              <ButtonV4
                v-if="canSubscribeCard"
                solid
                blue
                class="w-full"
                :is-loading="isCreatingCheckout"
                @click="subscribeWithCard"
              >
                <span class="i-lucide-credit-card w-5 h-5 mr-2" />
                {{ t('BILLING.GATEWAY.SUBSCRIBE_CARD') }}
              </ButtonV4>

              <ButtonV4
                v-if="
                  !canSubscribeCard &&
                  !showPaymentOptions &&
                  !hasPix &&
                  !(showBoletoDetails && boletoLine)
                "
                solid
                blue
                class="w-full"
                :is-loading="!hasAsaasCustomer && isCreatingCheckout"
                @click="
                  hasAsaasCustomer
                    ? (showPaymentOptions = true)
                    : payWithStripe()
                "
              >
                <span class="i-lucide-wallet w-5 h-5 mr-2" />
                {{
                  hasAsaasCustomer
                    ? t('BILLING.SIMPLE.PAY_NOW')
                    : t('BILLING.GATEWAY.PAY_WITH_CARD')
                }}
              </ButtonV4>

              <!-- Escolha da forma de pagamento -->
              <div v-if="showPaymentOptions" class="space-y-3">
                <p class="text-xs text-n-slate-10 uppercase tracking-wide mb-2">
                  {{ t('BILLING.GATEWAY.SELECT_PAYMENT') }}
                </p>

                <!-- PIX (Asaas) - só para clientes com Asaas -->
                <button
                  v-if="hasAsaasCustomer"
                  type="button"
                  class="w-full flex items-center gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-left"
                  :disabled="isGeneratingPix"
                  @click="generatePix"
                >
                  <span
                    class="w-10 h-10 rounded-lg bg-n-teal-3 flex items-center justify-center shrink-0"
                  >
                    <span class="i-lucide-qr-code w-5 h-5 text-n-teal-11" />
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ t('BILLING.PAYMENT.PIX_LABEL') }}
                    </p>
                    <p class="text-xs text-n-slate-11">
                      {{ t('BILLING.PAYMENT.PIX_HINT') }}
                    </p>
                  </div>
                  <Spinner v-if="isGeneratingPix" :size="20" />
                  <span
                    v-else
                    class="i-lucide-chevron-right w-5 h-5 text-n-slate-10"
                  />
                </button>

                <!-- Cartão de Crédito (Stripe) -->
                <button
                  type="button"
                  class="w-full flex items-center gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-left"
                  :disabled="isCreatingCheckout"
                  @click="payWithStripe"
                >
                  <span
                    class="w-10 h-10 rounded-lg bg-n-blue-3 flex items-center justify-center shrink-0"
                  >
                    <span class="i-lucide-credit-card w-5 h-5 text-n-blue-11" />
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ t('BILLING.GATEWAY.STRIPE_BADGE') }}
                    </p>
                    <p class="text-xs text-n-slate-11">
                      {{ t('BILLING.PAYMENT.CARD_HINT') }}
                    </p>
                  </div>
                  <Spinner v-if="isCreatingCheckout" :size="20" />
                  <span
                    v-else
                    class="i-lucide-chevron-right w-5 h-5 text-n-slate-10"
                  />
                </button>

                <!-- Boleto - só para clientes com Asaas -->
                <button
                  v-if="hasAsaasCustomer"
                  type="button"
                  class="w-full flex items-center gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-left"
                  :disabled="isGeneratingBoleto"
                  @click="generateBoleto"
                >
                  <span
                    class="w-10 h-10 rounded-lg bg-n-slate-3 flex items-center justify-center shrink-0"
                  >
                    <span class="i-lucide-file-text w-5 h-5 text-n-slate-11" />
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ t('BILLING.PAYMENT.BOLETO') }}
                    </p>
                    <p class="text-xs text-n-slate-11">
                      {{ t('BILLING.PAYMENT.BOLETO_HINT') }}
                    </p>
                  </div>
                  <Spinner v-if="isGeneratingBoleto" :size="20" />
                  <span
                    v-else
                    class="i-lucide-chevron-right w-5 h-5 text-n-slate-10"
                  />
                </button>

                <button
                  type="button"
                  class="text-xs text-n-slate-11 hover:text-n-slate-12 mt-2"
                  @click="showPaymentOptions = false"
                >
                  {{
                    hasPix || (showBoletoDetails && boletoLine)
                      ? t('BILLING.PAYMENT.BACK_TO_GENERATED')
                      : t('BILLING.PAYMENT.BACK')
                  }}
                </button>
              </div>

              <!-- PIX já gerado - mostrar QR code -->
              <div v-if="hasPix && !showPaymentOptions">
                <div class="rounded-xl border border-n-weak overflow-hidden">
                  <!-- Header PIX -->
                  <div
                    class="px-4 py-3 bg-n-teal-3 flex items-center justify-between cursor-pointer"
                    @click="showPixQrCode = !showPixQrCode"
                  >
                    <div class="flex items-center gap-2">
                      <span class="i-lucide-qr-code w-5 h-5 text-n-teal-11" />
                      <span class="text-sm font-semibold text-n-teal-11">
                        {{ t('BILLING.PAYMENT.PIX_GENERATED_TITLE') }}
                      </span>
                    </div>
                    <span
                      :class="
                        showPixQrCode
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="w-4 h-4 text-n-teal-11"
                    />
                  </div>

                  <!-- QR Code + Copiar -->
                  <div v-if="showPixQrCode" class="p-5 bg-white">
                    <p class="text-xs text-center text-n-slate-10 mb-4">
                      {{ t('BILLING.PAYMENT.PIX_SCAN_HINT') }}
                    </p>
                    <div class="flex justify-center mb-4">
                      <div
                        class="p-3 bg-white rounded-lg shadow-sm border border-n-weak"
                      >
                        <img
                          :src="`data:image/png;base64,${invoice.pix_qr_code}`"
                          :alt="t('BILLING.MY_PLAN.PIX_QR_ALT')"
                          class="w-52 h-52"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors"
                      :class="
                        pixCopied
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-teal-9 text-white hover:bg-n-teal-10'
                      "
                      @click="copyPixPayload"
                    >
                      <span
                        :class="pixCopied ? 'i-lucide-check' : 'i-lucide-copy'"
                        class="w-4 h-4"
                      />
                      {{
                        pixCopied
                          ? t('BILLING.INVOICE.COPIED')
                          : t('BILLING.INVOICE.COPY_PIX')
                      }}
                    </button>
                    <p
                      v-if="invoice.pix_expiration"
                      class="text-xs text-center text-n-slate-10 mt-3"
                    >
                      {{ t('BILLING.PAYMENT.PIX_VALID_UNTIL') }}
                      {{
                        format(
                          parseISO(invoice.pix_expiration),
                          "dd/MM/yyyy 'às' HH:mm",
                          { locale: ptBR }
                        )
                      }}
                    </p>
                  </div>
                </div>

                <!-- Trocar forma de pagamento -->
                <button
                  type="button"
                  class="w-full text-center text-xs text-n-slate-11 hover:text-n-slate-12 mt-3 py-1 transition-colors"
                  @click="showPaymentOptions = true"
                >
                  {{ t('BILLING.PAYMENT.CHOOSE_ANOTHER') }}
                </button>
              </div>

              <!-- Boleto já gerado -->
              <div
                v-if="showBoletoDetails && boletoLine && !showPaymentOptions"
              >
                <div class="rounded-xl border border-n-weak overflow-hidden">
                  <!-- Header Boleto -->
                  <div class="px-4 py-3 bg-n-slate-3">
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-file-text w-5 h-5 text-n-slate-11"
                      />
                      <span class="text-sm font-semibold text-n-slate-12">
                        {{ t('BILLING.PAYMENT.BOLETO_GENERATED_TITLE') }}
                      </span>
                    </div>
                  </div>

                  <div class="p-4 bg-n-alpha-1">
                    <p
                      class="text-xs text-n-slate-11 font-mono break-all mb-3 p-3 bg-white rounded-lg border border-n-weak"
                    >
                      {{ boletoLine }}
                    </p>
                    <div class="flex gap-2">
                      <ButtonV4
                        faded
                        slate
                        class="flex-1"
                        @click="copyBoletoLine"
                      >
                        <span
                          :class="
                            boletoCopied ? 'i-lucide-check' : 'i-lucide-copy'
                          "
                          class="w-4 h-4 mr-2"
                        />
                        {{
                          boletoCopied
                            ? t('BILLING.INVOICE.COPIED')
                            : t('BILLING.PAYMENT.COPY_LINE')
                        }}
                      </ButtonV4>
                      <a
                        v-if="boletoUrl"
                        :href="boletoUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
                      >
                        <span class="i-lucide-external-link w-4 h-4" />
                        {{ t('BILLING.PAYMENT.OPEN_BOLETO') }}
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Trocar forma de pagamento -->
                <button
                  type="button"
                  class="w-full text-center text-xs text-n-slate-11 hover:text-n-slate-12 mt-3 py-1 transition-colors"
                  @click="showPaymentOptions = true"
                >
                  {{ t('BILLING.PAYMENT.CHOOSE_ANOTHER') }}
                </button>
              </div>
            </div>

            <!-- Sem fatura pendente - Mostrar próximo pagamento -->
            <div v-else>
              <div class="text-center py-4">
                <span
                  class="i-lucide-check-circle w-12 h-12 text-n-teal-11 mx-auto mb-3"
                />
                <p class="text-lg font-semibold text-n-slate-12 mb-1">
                  {{ t('BILLING.PAYMENT.UP_TO_DATE') }}
                </p>
                <p class="text-sm text-n-slate-11 mb-4">
                  {{ t('BILLING.PAYMENT.NO_PENDING_INVOICES') }}
                </p>
              </div>

              <!-- Emitir Nota Fiscal (para fatura paga) -->
              <div v-if="isPaid" class="mb-4">
                <ButtonV4
                  faded
                  slate
                  class="w-full"
                  :is-loading="isRequestingNfse"
                  :disabled="nfseRequested"
                  @click="requestNfse"
                >
                  <span
                    :class="
                      nfseRequested ? 'i-lucide-check' : 'i-lucide-file-check'
                    "
                    class="w-5 h-5 mr-2"
                  />
                  {{
                    nfseRequested
                      ? t('BILLING.PAYMENT.NFSE_REQUESTED_LABEL')
                      : t('BILLING.PAYMENT.NFSE_ISSUE')
                  }}
                </ButtonV4>
              </div>

              <div class="p-4 rounded-xl bg-n-alpha-2 mb-4">
                <p class="text-xs text-n-slate-10 uppercase tracking-wide mb-2">
                  {{ t('BILLING.PAYMENT.NEXT_PAYMENT') }}
                </p>
                <div class="flex items-center justify-between mb-1">
                  <span class="text-sm text-n-slate-11">{{
                    t('BILLING.INVOICE.PERIOD')
                  }}</span>
                  <span class="font-medium text-n-slate-12 capitalize">{{
                    formattedNextPaymentDate
                  }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-n-slate-11">{{
                    t('BILLING.PAYMENT.ESTIMATED_AMOUNT')
                  }}</span>
                  <span class="font-medium text-n-slate-12 tabular-nums">{{
                    formatCurrency(effectivePrice)
                  }}</span>
                </div>
              </div>

              <ButtonV4
                v-if="canGenerateInvoice"
                faded
                slate
                class="w-full mb-3"
                :is-loading="isGeneratingInvoice"
                @click="generateInvoice"
              >
                {{ t('BILLING.SIMPLE.GENERATE_INVOICE') }}
              </ButtonV4>
            </div>
          </div>
        </div>

        <!-- ==========================================
             2. MEU PLANO: recursos com uso + auto-compra
        ========================================== -->
        <PlanResourcesCard />

        <!-- ==========================================
             3. CONSUMO (crédito): saldo + custo por uso + extrato
        ========================================== -->
        <div
          class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden"
        >
          <div class="px-6 py-4 border-b border-n-weak bg-n-alpha-1">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('BILLING.CONSUMPTION.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11 mt-0.5">
              {{ t('BILLING.CONSUMPTION.DESCRIPTION') }}
            </p>
          </div>
          <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              class="rounded-xl border border-n-weak p-4 flex flex-col justify-between bg-n-alpha-1"
            >
              <div>
                <p class="text-xs text-n-slate-10">
                  {{ t('BILLING.CONSUMPTION.BALANCE_LABEL') }}
                </p>
                <p class="text-3xl font-bold text-n-slate-12 mt-1 tabular-nums">
                  {{ formatCreditBalance(creditBalanceBrl) }}
                </p>
              </div>
              <div class="mt-4">
                <RechargeButton
                  :label="t('BILLING.CONSUMPTION.RECHARGE')"
                  :is-card="canSubscribeCard || isStripeLinked"
                />
              </div>
            </div>

            <div class="space-y-2 text-sm">
              <p class="text-xs text-n-slate-10 uppercase tracking-wide mb-1">
                {{ t('BILLING.CONSUMPTION.COST_PER_USE') }}
              </p>
              <div class="flex justify-between border-b border-n-weak pb-2">
                <span class="text-n-slate-11">
                  {{ t('BILLING.CONSUMPTION.VOICE_AI_LABEL') }}
                </span>
                <span class="font-medium text-n-slate-12 tabular-nums">
                  {{ t('BILLING.CONSUMPTION.VOICE_AI_PRICE') }}
                </span>
              </div>
              <div class="flex justify-between border-b border-n-weak pb-2">
                <span class="text-n-slate-11">
                  {{ t('BILLING.CONSUMPTION.PROSPECTING_LABEL') }}
                </span>
                <span class="font-medium text-n-slate-12 tabular-nums">
                  {{ t('BILLING.CONSUMPTION.PROSPECTING_PRICE') }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10 pt-1">
                {{ t('BILLING.CONSUMPTION.INCLUDED_NOTICE') }}
              </p>
            </div>
          </div>
        </div>

        <ConsumptionStatement />

        <!-- ==========================================
             4. FATURAS: link para o histórico
        ========================================== -->
        <div
          class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden"
        >
          <button
            type="button"
            class="w-full flex items-center justify-between px-6 py-4 hover:bg-n-alpha-1 transition-colors"
            @click="goToHistory"
          >
            <div class="text-left">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('BILLING.INVOICE.HISTORY_TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ t('BILLING.INVOICE.HISTORY_DESCRIPTION') }}
              </p>
            </div>
            <span
              class="i-lucide-chevron-right w-5 h-5 text-n-slate-10 shrink-0"
            />
          </button>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
